import { appConst } from "../AppConst";
import { useAppDispatch } from "../hooks/useAppDispatch";
import { useAuth } from "../hooks/useAuth";
import { NotifyHelper } from "../helpers/toast";
import { rootActions } from "../state/actions/rootActions";
import { ChangePasswordForm } from "./ChangePasswordForm";

/**
 * Màn hình BUỘC đổi mật khẩu — hiển thị thay cho toàn bộ app khi SV đăng nhập bằng mật khẩu yếu
 * (must_change_password). Không cho hủy/điều hướng (route guard chặn); chỉ 2 lối: đổi mật khẩu mạnh
 * (mở khoá app) hoặc đăng xuất. Xem ProtectedRoute + AuthService (claim pwd_must_change).
 *
 * Giao diện warm/trẻ trung cho cổng SV: nền lavender, badge gradient, thông điệp thân thiện.
 */
export const ForcePasswordChange = () => {
    const dispatch = useAppDispatch();
    const { user, passwordWeakReason } = useAuth();
    const { webName } = appConst.branding;

    const handleLogout = () => dispatch(rootActions.auth.LOGOUT());

    const firstName = (user?.ho_ten || "").trim().split(/\s+/).pop() || "bạn";

    return (
        <div className="fpc-wrap">
            <style>{`
                .fpc-wrap {
                    position:relative; min-height:100vh; width:100%; overflow:hidden;
                    display:flex; align-items:center; justify-content:center; padding:24px;
                    background:linear-gradient(160deg,#e8f1ff 0%,#eef5ff 48%,#f3f8ff 100%);
                    font-family:system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans",Arial,sans-serif;
                }
                /* Bong bóng trang trí mềm */
                .fpc-wrap::before, .fpc-wrap::after {
                    content:""; position:absolute; border-radius:50%; filter:blur(8px); opacity:.55; z-index:0;
                }
                .fpc-wrap::before { width:340px; height:340px; top:-90px; right:-70px;
                    background:radial-gradient(circle at 30% 30%,#bcd9ff,#dcebff 70%); }
                .fpc-wrap::after { width:300px; height:300px; bottom:-80px; left:-60px;
                    background:radial-gradient(circle at 30% 30%,#cfe4ff,#e6f1ff 70%); }

                .fpc-card {
                    position:relative; z-index:1; width:100%; max-width:470px; box-sizing:border-box;
                    background:#fff; border-radius:24px; padding:34px 30px 26px;
                    box-shadow:0 24px 60px rgba(0,90,200,.18); border:1px solid rgba(255,255,255,.8);
                    animation:fpcIn .45s cubic-bezier(.2,.7,.3,1);
                }
                @keyframes fpcIn { from{opacity:0; transform:translateY(14px)} to{opacity:1; transform:translateY(0)} }

                .fpc-badge {
                    width:76px; height:76px; margin:0 auto 18px; border-radius:22px;
                    display:flex; align-items:center; justify-content:center;
                    background:linear-gradient(135deg,#2b93ff 0%,#007bff 100%);
                    box-shadow:0 12px 26px rgba(0,123,255,.4);
                }
                .fpc-badge i { color:#fff; font-size:32px; }

                .fpc-title { text-align:center; font-size:21px; font-weight:800; color:#12305a; margin:0 0 6px; letter-spacing:-.2px; }
                .fpc-sub { text-align:center; font-size:14px; line-height:1.5; color:#6b6f86; margin:0 auto 20px; max-width:360px; }
                .fpc-sub b { color:#007bff; }

                .fpc-reason {
                    display:flex; gap:10px; align-items:flex-start;
                    background:#fff7ed; border:1px solid #fed7aa; color:#9a3412;
                    border-radius:12px; padding:11px 14px; font-size:13px; line-height:1.45; margin-bottom:20px;
                }
                .fpc-reason i { color:#f59e0b; font-size:15px; margin-top:1px; }

                .fpc-foot { text-align:center; margin-top:18px; padding-top:16px; border-top:1px solid #f0f1f6; }
                .fpc-foot .brand { display:block; font-size:12px; color:#9aa0b4; margin-bottom:8px; letter-spacing:.3px; }
                .fpc-logout {
                    background:transparent; border:none; color:#6b7280; cursor:pointer; font-size:13px;
                    font-weight:600; text-decoration:none; font-family:inherit;
                }
                .fpc-logout:hover { color:#007bff; text-decoration:underline; }
            `}</style>

            <div className="fpc-card">
                <div className="fpc-badge">
                    <i className="fa fa-lock" aria-hidden="true" />
                </div>

                <h1 className="fpc-title">Đổi mật khẩu để tiếp tục</h1>
                <p className="fpc-sub">
                    Chào <b>{firstName}</b>, để bảo vệ tài khoản, bạn cần đặt một mật khẩu mạnh hơn
                    trước khi xem lớp và đăng ký.
                </p>

                <div className="fpc-reason">
                    <i className="fa fa-exclamation-circle" aria-hidden="true" />
                    <span>
                        {passwordWeakReason
                            ? passwordWeakReason
                            : "Mật khẩu hiện tại của bạn chưa đủ an toàn."}
                    </span>
                </div>

                <ChangePasswordForm
                    maSv={user?.ma_sv}
                    submitLabel="Đổi mật khẩu & tiếp tục"
                    onSuccess={(res) => {
                        NotifyHelper.Success(res.message);
                        dispatch(
                            rootActions.auth.PASSWORD_CHANGED({
                                access_token: res.access_token || "",
                                expires_at: res.expires_at || "",
                            })
                        );
                    }}
                />

                <div className="fpc-foot">
                    {webName && <span className="brand">{webName}</span>}
                    <button type="button" className="fpc-logout" onClick={handleLogout}>
                        Không phải bạn? Đăng xuất
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ForcePasswordChange;
